import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Input, Tag, Select, Modal, message, Spin, Row, Col, Card, Dropdown, Badge,
} from "antd";
import {
  SearchOutlined,
  FilePdfOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
  StopOutlined,
  MoreOutlined,
  BarsOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const Wrapper = styled.div`
  .page-header {
    margin-bottom: 24px;
    h1 { font-size: 22px; font-weight: 700; color: #014c75; margin: 0 0 4px; }
    p { color: #666; font-size: 14px; margin: 0; }
  }
  .stat-card { border-radius: 8px; }
  .stat-inner { display: flex; align-items: center; gap: 14px; }
  .stat-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;
  }
  .stat-info h3 { margin: 0; font-size: 22px; font-weight: 700; color: #014c75; line-height: 1.2; }
  .stat-info p { margin: 0; font-size: 12px; color: #888; }
  .filter-row {
    display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
  }
  .ant-table { background: white; border-radius: 8px; }
  .view-toggle-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 6px; border: 1px solid #d9d9d9;
    cursor: pointer; background: #fff; font-size: 16px; color: #595959;
    transition: all 0.2s;
    &:hover { border-color: #014c75; color: #014c75; }
    &.active { background: #014c75; border-color: #014c75; color: #fff; }
  }
`;

const KanbanBoard = styled.div`
  display: flex; gap: 16px; overflow-x: auto; padding-bottom: 16px;
  align-items: flex-start; min-height: 200px;
`;

const KanbanColumn = styled.div`
  flex: 0 0 270px; background: #f7f8fa; border-radius: 10px;
  border-top: 3px solid ${(p) => p.$color}; padding: 12px; min-height: 160px;
`;

const KanbanColHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
  .col-title { font-size: 13px; font-weight: 600; color: ${(p) => p.$color}; }
`;

const KanbanCard = styled.div`
  background: #fff; border-radius: 8px; border: 1px solid #e8eaf0;
  padding: 12px; margin-bottom: 10px; box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.09); }
`;

const CardHeader = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 10px; gap: 6px;
`;

const CardName = styled.a`
  font-size: 13px; font-weight: 600; color: #014c75 !important; line-height: 1.3;
  flex: 1; &:hover { text-decoration: underline; }
`;

const CardField = styled.div`
  display: flex; align-items: baseline; gap: 4px; margin-bottom: 5px; font-size: 12px;
  .field-label { color: #aaa; min-width: 80px; flex-shrink: 0; }
  .field-value { color: #3d3d3d; font-weight: 500; word-break: break-word; }
`;

const KANBAN_COLUMNS = [
  { key: "Under Review",        label: "Under Review",        color: "#1677ff" },
  { key: "Shortlisted",         label: "Shortlisted",         color: "#13c2c2" },
  { key: "Interview Scheduled", label: "Interview Scheduled", color: "#fa8c16" },
  { key: "Hired",               label: "Hired",               color: "#52c41a" },
  { key: "Rejected",            label: "Rejected",            color: "#ff4d4f" },
  { key: "Withdrawn",           label: "Withdrawn",           color: "#8c8c8c" },
];

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return `₹ ${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "2-digit" });
};

const mapStatusToDisplay = (status) => {
  const statusMap = {
    submitted: "Under Review",
    shortlisted: "Shortlisted",
    interview_scheduled: "Interview Scheduled",
    hired: "Hired",
    rejected: "Rejected",
    withdrawn: "Withdrawn",
  };
  return statusMap[status] || status;
};

const STATUS_COLOR = {
  "Under Review": "blue",
  "Shortlisted": "cyan",
  "Interview Scheduled": "orange",
  "Hired": "green",
  "Rejected": "red",
  "Withdrawn": "default",
};

const STAT_CARDS = [
  { key: "total",     label: "Total Submitted",      color: "#e6f7ff", iconColor: "#1890ff", icon: <TeamOutlined /> },
  { key: "review",    label: "Under Review",          color: "#e6f7ff", iconColor: "#1890ff", icon: <TeamOutlined /> },
  { key: "interview", label: "Interview Scheduled",   color: "#fff7e6", iconColor: "#fa8c16", icon: <VideoCameraOutlined /> },
  { key: "hired",     label: "Hired",                 color: "#f6ffed", iconColor: "#52c41a", icon: <CheckCircleOutlined /> },
  { key: "rejected",  label: "Rejected",              color: "#fff1f0", iconColor: "#ff4d4f", icon: <CloseCircleOutlined /> },
  { key: "withdrawn", label: "Withdrawn",             color: "#f5f5f5", iconColor: "#8c8c8c", icon: <CloseCircleOutlined /> },
];

const AdminSubmittedProfiles = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [jobOptions, setJobOptions] = useState([]);
  const [viewMode, setViewMode] = useState("kanban");

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      const jobsRes = await axios.post(
        API_CONST.GET_JOB_REQUIREMENTS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const jobs = jobsRes.data.Response || [];
      setJobOptions(jobs.map((j) => ({ id: j.id, title: j.role })));

      const allProfiles = [];
      await Promise.all(
        jobs.map(async (job) => {
          try {
            const appRes = await axios.post(
              API_CONST.GET_JOB_APPLICATIONS,
              { jobId: job.id },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const apps = appRes.data.applications || [];
            apps.forEach((app) => {
              allProfiles.push({
                key: app.applicationId,
                applicationId: app.applicationId,
                talentId: app.talentId,
                profileId: app.talentId,
                name: app.talent?.name || "Unknown",
                email: app.talent?.email || "-",
                contact: app.talent?.phone || "-",
                jobId: job.id,
                jobTitle: job.role,
                customerOrg: job.customerOrganization || "-",
                partnerOrg: app.partnerOrganization || "-",
                experience: app.talent?.experience || "-",
                primarySkill: (app.talent?.primarySkills || []).filter(Boolean).join(", ") || "-",
                partnerCost: formatCurrency(app.talent?.partnerRate),
                settledCost: formatCurrency(app.talent?.settledCost),
                clientRate: formatCurrency(app.talent?.clientRate),
                submittedOn: formatDate(app.submittedAt),
                status: mapStatusToDisplay(app.status),
                rawStatus: app.status,
                resumeUrl: app.talent?.resumeUrl,
                hiringNotes: app.notes || null,
              });
            });
          } catch (_) {}
        })
      );
      setProfiles(allProfiles);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      message.error("Failed to load submitted profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const updateStatus = async (applicationId, newStatus) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_CONST.UPDATE_JOB_APPLICATION}/${applicationId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfiles((prev) =>
        prev.map((p) =>
          p.applicationId === applicationId
            ? { ...p, status: mapStatusToDisplay(newStatus), rawStatus: newStatus }
            : p
        )
      );
      message.success(`Status updated to ${mapStatusToDisplay(newStatus)}`);
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleScheduleInterview = (record) => {
    Modal.confirm({
      title: "Schedule Interview",
      content: `Schedule an interview for ${record.name}?`,
      okText: "Schedule",
      onOk: () => updateStatus(record.applicationId, "interview_scheduled"),
    });
  };

  const handleHire = (record) => {
    Modal.confirm({
      title: "Hire Talent",
      content: `Are you sure you want to hire ${record.name}?`,
      okText: "Hire",
      okButtonProps: { style: { backgroundColor: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: () => updateStatus(record.applicationId, "hired"),
    });
  };

  const handleReject = (record) => {
    Modal.confirm({
      title: "Reject Application",
      content: `Are you sure you want to reject ${record.name}'s application?`,
      okText: "Reject",
      okButtonProps: { danger: true },
      onOk: () => updateStatus(record.applicationId, "rejected"),
    });
  };

  const [revertModalVisible, setRevertModalVisible] = useState(false);
  const [revertRecord, setRevertRecord] = useState(null);
  const [revertStatus, setRevertStatus] = useState("submitted");
  const [revertLoading, setRevertLoading] = useState(false);

  const handleCancelHire = (record) => {
    setRevertRecord(record);
    setRevertStatus("submitted");
    setRevertModalVisible(true);
  };

  const handleRevertHireConfirm = async () => {
    if (!revertRecord) return;
    setRevertLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_CONST.UPDATE_JOB_APPLICATION}/${revertRecord.applicationId}`,
        { status: revertStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfiles((prev) =>
        prev.map((p) =>
          p.applicationId === revertRecord.applicationId
            ? { ...p, status: mapStatusToDisplay(revertStatus), rawStatus: revertStatus }
            : p
        )
      );
      message.success(`Hire cancelled — status reverted to ${mapStatusToDisplay(revertStatus)}`);
      setRevertModalVisible(false);
      setRevertRecord(null);
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Failed to revert hire status";
      message.error(errMsg);
    } finally {
      setRevertLoading(false);
    }
  };

  const handleViewResume = async (record) => {
    if (!record.resumeUrl) { message.warning("No resume available"); return; }
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: record.resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.downloadUrl) window.open(res.data.downloadUrl, "_blank");
      else message.error("Could not get download URL");
    } catch {
      message.error("Failed to open resume");
    }
  };

  const columns = [
    {
      title: "Talent ID", dataIndex: "profileId", key: "profileId", width: 130,
    },
    {
      title: "Name", dataIndex: "name", key: "name", width: 150,
      render: (text, record) => (
        <a
          onClick={() => navigate(`/admin/talent-profiles/details/${record.talentId}`, {
            state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
          })}
          style={{ color: "#1890ff", cursor: "pointer", fontWeight: 500 }}
        >
          {text}
        </a>
      ),
    },
    { title: "Email", dataIndex: "email", key: "email", width: 200 },
    { title: "Contact", dataIndex: "contact", key: "contact", width: 140 },
    {
      title: "Job ID", dataIndex: "jobId", key: "jobId", width: 110,
      render: (id) => (
        <a
          onClick={() => navigate(`/admin/job-requirments/job-details/${id}`, {
            state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
          })}
          style={{ color: "#1890ff", cursor: "pointer", fontWeight: 500 }}
        >
          {id}
        </a>
      ),
    },
    { title: "Job Title", dataIndex: "jobTitle", key: "jobTitle", width: 180 },
    {
      title: "Customer Org", dataIndex: "customerOrg", key: "customerOrg", width: 160,
      render: (org) => org && org !== "-" ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    { title: "Experience", dataIndex: "experience", key: "experience", width: 120 },
    { title: "Primary Skill", dataIndex: "primarySkill", key: "primarySkill", width: 200, ellipsis: true },
    {
      title: "Partner Org", dataIndex: "partnerOrg", key: "partnerOrg", width: 160,
      render: (org) => org && org !== "-" ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    { title: "Partner Cost", dataIndex: "partnerCost", key: "partnerCost", width: 130 },
    { title: "Settled Cost", dataIndex: "settledCost", key: "settledCost", width: 130 },
    { title: "Client Rate", dataIndex: "clientRate", key: "clientRate", width: 130 },
    { title: "Submitted On", dataIndex: "submittedOn", key: "submittedOn", width: 110 },
    {
      title: "Status", dataIndex: "status", key: "status", width: 160,
      render: (status) => <Tag color={STATUS_COLOR[status] || "default"}>{status}</Tag>,
    },
    {
      title: "Action", key: "action", width: 80, fixed: "right",
      render: (_, record) => {
        const isHired = record.rawStatus === "hired";
        const isRejected = record.rawStatus === "rejected";
        const isInterviewScheduled = record.rawStatus === "interview_scheduled";
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: "view",
                  label: "View Profile",
                  onClick: () => navigate(`/admin/talent-profiles/details/${record.talentId}`, {
                    state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
                  }),
                },
                { key: "resume", label: "View Resume", icon: <FilePdfOutlined />, onClick: () => handleViewResume(record) },
                { key: "interview", label: "Schedule Interview", icon: <VideoCameraOutlined />, disabled: isHired || isRejected || isInterviewScheduled, onClick: () => handleScheduleInterview(record) },
                { key: "hire", label: "Hire", icon: <CheckCircleOutlined />, disabled: isHired || isRejected, onClick: () => handleHire(record) },
                { key: "reject", label: "Reject", icon: <CloseCircleOutlined />, danger: true, disabled: isHired || isRejected, onClick: () => handleReject(record) },
              ],
            }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer" }} />
          </Dropdown>
        );
      },
    },
  ];

  const filteredData = profiles.filter((p) => {
    const matchSearch =
      p.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      p.profileId?.toLowerCase().includes(searchText.toLowerCase()) ||
      p.jobTitle?.toLowerCase().includes(searchText.toLowerCase());
    const matchJob = selectedJob === "all" || p.jobId === selectedJob;
    const matchStatus = selectedStatus === "all" || p.status === selectedStatus;
    return matchSearch && matchJob && matchStatus;
  });

  const stats = {
    total: profiles.length,
    review: profiles.filter((p) => p.rawStatus === "submitted").length,
    interview: profiles.filter((p) => p.rawStatus === "interview_scheduled").length,
    hired: profiles.filter((p) => p.rawStatus === "hired").length,
    rejected: profiles.filter((p) => p.rawStatus === "rejected").length,
    withdrawn: profiles.filter((p) => p.rawStatus === "withdrawn").length,
  };

  const getActionItems = (record) => {
    const isHired = record.rawStatus === "hired";
    const isRejected = record.rawStatus === "rejected";
    const isInterviewScheduled = record.rawStatus === "interview_scheduled";
    return [
      { key: "view", label: "View Profile", onClick: () => navigate(`/admin/talent-profiles/details/${record.talentId}`, { state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" } }) },
      { key: "resume", label: "View Resume", icon: <FilePdfOutlined />, onClick: () => handleViewResume(record) },
      { key: "interview", label: "Schedule Interview", icon: <VideoCameraOutlined />, disabled: isHired || isRejected || isInterviewScheduled, onClick: () => handleScheduleInterview(record) },
      { key: "hire", label: "Hire", icon: <CheckCircleOutlined />, disabled: isHired || isRejected, onClick: () => handleHire(record) },
      { key: "reject", label: "Reject", icon: <CloseCircleOutlined />, danger: true, disabled: isHired || isRejected, onClick: () => handleReject(record) },
      ...(isHired ? [{ type: "divider" }] : []),
      ...(isHired ? [{
        key: "cancel-hire",
        label: "Cancel Hire",
        icon: <RollbackOutlined />,
        danger: true,
        onClick: () => handleCancelHire(record),
      }] : []),
    ];
  };

  const renderKanban = () => (
    <KanbanBoard>
      {KANBAN_COLUMNS.map((col) => {
        const cards = filteredData.filter((p) => p.status === col.key);
        return (
          <KanbanColumn key={col.key} $color={col.color}>
            <KanbanColHeader $color={col.color}>
              <span className="col-title">{col.label}</span>
              <Badge count={cards.length} style={{ backgroundColor: col.color, fontSize: 11, boxShadow: "none" }} showZero />
            </KanbanColHeader>
            {cards.length === 0 && (
              <div style={{ textAlign: "center", color: "#bbb", fontSize: 12, padding: "24px 0" }}>No profiles</div>
            )}
            {cards.map((record) => (
              <KanbanCard key={record.key}>
                <CardHeader>
                  <CardName href="#" onClick={(e) => { e.preventDefault(); navigate(`/admin/talent-profiles/details/${record.talentId}`, { state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" } }); }}>
                    {record.name}
                  </CardName>
                  <Dropdown menu={{ items: getActionItems(record) }} trigger={["click"]}>
                    <MoreOutlined style={{ cursor: "pointer", fontSize: 16, color: "#888", flexShrink: 0, padding: "2px 4px" }} />
                  </Dropdown>
                </CardHeader>
                <CardField>
                  <span className="field-label">Job</span>
                  <span className="field-value">
                    <a href="#" style={{ color: "#014c75" }} onClick={(e) => { e.preventDefault(); navigate(`/admin/job-requirments/job-details/${record.jobId}`, { state: { from: "/admin/submitted-profiles", fromLabel: "Back to Submitted Profiles" } }); }}>
                      {record.jobId}
                    </a>
                  </span>
                </CardField>
                <CardField><span className="field-label">Job Title</span><span className="field-value">{record.jobTitle}</span></CardField>
                <CardField><span className="field-label">Partner Org</span><span className="field-value">{record.partnerOrg}</span></CardField>
                <CardField><span className="field-label">Experience</span><span className="field-value">{record.experience}</span></CardField>
                <CardField><span className="field-label">Partner Cost</span><span className="field-value">{record.partnerCost}</span></CardField>
                <CardField><span className="field-label">Submitted</span><span className="field-value">{record.submittedOn}</span></CardField>
              </KanbanCard>
            ))}
          </KanbanColumn>
        );
      })}
    </KanbanBoard>
  );

  return (
    <Wrapper>
      <div className="page-header">
        <h1>Submitted Profiles</h1>
        <p>Track and manage all talent profiles submitted across job requirements</p>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {STAT_CARDS.map((card) => (
          <Col xs={24} sm={12} md={8} lg={4} xl={4} key={card.key} style={{ minWidth: 180 }}>
            <Card className="stat-card" bodyStyle={{ padding: "16px 20px" }}>
              <div className="stat-inner">
                <div className="stat-icon" style={{ background: card.color, color: card.iconColor }}>
                  {card.icon}
                </div>
                <div className="stat-info">
                  <h3>{stats[card.key]}</h3>
                  <p>{card.label}</p>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="filter-row">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name, ID or job title"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 280 }}
        />
        <Select value={selectedJob} onChange={setSelectedJob} style={{ width: 240 }}>
          <Option value="all">All Jobs</Option>
          {jobOptions.map((j) => (
            <Option key={j.id} value={j.id}>{j.id} — {j.title}</Option>
          ))}
        </Select>
        <Select value={selectedStatus} onChange={setSelectedStatus} style={{ width: 200 }}>
          <Option value="all">All Statuses</Option>
          <Option value="Under Review">Under Review</Option>
          <Option value="Shortlisted">Shortlisted</Option>
          <Option value="Interview Scheduled">Interview Scheduled</Option>
          <Option value="Hired">Hired</Option>
          <Option value="Rejected">Rejected</Option>
          <Option value="Withdrawn">Withdrawn</Option>
        </Select>
        <span style={{ color: "#888", fontSize: 13, marginLeft: "auto" }}>
          {filteredData.length} profile{filteredData.length !== 1 ? "s" : ""}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <span className={`view-toggle-btn${viewMode === "table" ? " active" : ""}`} onClick={() => setViewMode("table")} title="Table view"><BarsOutlined /></span>
          <span className={`view-toggle-btn${viewMode === "kanban" ? " active" : ""}`} onClick={() => setViewMode("kanban")} title="Kanban view"><AppstoreOutlined /></span>
        </div>
      </div>

      <Spin spinning={loading}>
        {viewMode === "table" ? (
          <Card bodyStyle={{ padding: 0 }}>
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="key"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} profiles`,
              }}
              scroll={{ x: 1800 }}
              locale={{ emptyText: loading ? " " : "No submitted profiles found" }}
            />
          </Card>
        ) : (
          renderKanban()
        )}
      </Spin>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <RollbackOutlined style={{ color: "#ff4d4f", fontSize: 17 }} />
            <span>Cancel Hire</span>
          </div>
        }
        open={revertModalVisible}
        onOk={handleRevertHireConfirm}
        onCancel={() => { setRevertModalVisible(false); setRevertRecord(null); }}
        okText="Confirm Revert"
        okButtonProps={{ danger: true, loading: revertLoading }}
        cancelText="Go Back"
        width={480}
      >
        {revertRecord && (
          <div>
            <div style={{ padding: "10px 14px", background: "#fff1f0", borderRadius: 6, border: "1px solid #ffccc7", marginBottom: 18 }}>
              <p style={{ margin: 0, color: "#cf1322", fontSize: 13 }}>
                <strong>Note:</strong> This will delete the auto-created draft engagement record and revert the talent's status. If an active hiring record already exists, this action will be blocked — the contract must be terminated from the Talents Hired section instead.
              </p>
            </div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <p style={{ margin: 0, fontWeight: 600 }}>{revertRecord.name}</p>
              <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{revertRecord.jobTitle} — {revertRecord.jobId}</p>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Revert status to
              </label>
              <Select
                value={revertStatus}
                onChange={setRevertStatus}
                style={{ width: "100%" }}
                options={[
                  { value: "submitted",            label: "Under Review" },
                  { value: "shortlisted",          label: "Shortlisted" },
                  { value: "interview_scheduled",  label: "Interview Scheduled" },
                ]}
              />
            </div>
          </div>
        )}
      </Modal>
    </Wrapper>
  );
};

export default AdminSubmittedProfiles;
